import { storage } from '../../lib/firebase';
import { ref, uploadBytes, deleteObject, getDownloadURL } from 'firebase/storage';
import { convertToWebP, generateCleanFilename } from '../../utils/image';
import { isProduction } from '../../config/environment';

interface UseOrganizationLogoReturn {
  uploadLogo: (file: File, orgId: string, currentLogoUrl?: string) => Promise<string>;
}

export const useOrganizationLogo = (): UseOrganizationLogoReturn => {
  const uploadLogo = async (
    file: File,
    orgId: string,
    currentLogoUrl?: string
  ): Promise<string> => {
    try {
      // Convert and compress image to PNG
      const pngBlob = await convertToWebP(file);

      // Generate clean filename with organization ID in path
      const filename = generateCleanFilename(file.name, orgId);
      const fullPath = isProduction()
        ? `logos/${filename}` // Production: logos folder in heyisa-media bucket
        : `speculo-onboarding/logos/${filename}`; // Staging/Dev: speculo-onboarding bucket

      // Delete old logo if it exists
      if (currentLogoUrl) {
        if (currentLogoUrl.includes('logos')) {
          try {
            // Extract the path from emulator URL format
            const match = currentLogoUrl.match(/logos%2F(.*?)(?:\?|$)/);

            if (match && match[1]) {
              const currentPath = isProduction()
                ? `heyisa-media/logos/${decodeURIComponent(match[1])}`
                : `logos/${decodeURIComponent(match[1])}`;
              const currentRef = ref(storage, currentPath);
              await deleteObject(currentRef);
            }
          } catch (error) {
            console.warn('Could not delete old logo:', error);
            // Continue with upload even if delete fails
          }
        }
      }

      // Create storage reference based on environment
      const storageRef = isProduction()
        ? ref(storage, `heyisa-media/${fullPath}`) // Production: heyisa-media/logos/
        : ref(storage, fullPath); // Staging/Dev: speculo-onboarding/logos/
      await uploadBytes(storageRef, pngBlob, {
        contentType: 'image/png',
      });

      // Get the download URL
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Error handling logo upload:', error);
      throw new Error('Failed to upload logo');
    }
  };

  return { uploadLogo };
};
